import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Search,
  CalendarDays,
  CalendarRange,
  Clock,
} from 'lucide-react';
import api from '../../services/api';
import {
  fetchAdminOrSuperadminTaskAnalytics,
  getTaskAnalyticsErrorMessage,
} from '../../services/taskAnalyticsApi';
import LoadingSpinner from '../common/LoadingSpinner';
import { StatusCountsChart, PriorityCountsChart } from '../shared/TaskAnalyticsCharts';
import { getUserIdentifier, normalizeUserProfiles } from '../../utils/userProfile';

const FREQUENCY_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const TASK_FREQUENCY_LABEL = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

function hrSignal(rate, overdue, rejectedReviewCount) {
  const rejections = typeof rejectedReviewCount === 'number' ? rejectedReviewCount : null;
  const hasOverdue = typeof overdue === 'number';
  const manySendbacks = rejections != null && rejections >= 4;

  const reviewNeeded = () => ({ label: 'Review needed', className: 'bg-red-50 text-red-800 border-red-200' });
  const fair = () => ({ label: 'Fair', className: 'bg-amber-50 text-amber-800 border-amber-200' });
  const onTrack = () => ({ label: 'On track', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' });

  if (hasOverdue) {
    if (rate < 50 || overdue >= 3 || manySendbacks) return reviewNeeded();
    if (rate >= 75 && overdue === 0 && !manySendbacks) return onTrack();
    if (rejections != null && rejections >= 2) return fair();
    return fair();
  }
  if (rate < 50 || manySendbacks) return reviewNeeded();
  if (rate >= 75 && (rejections == null || rejections <= 1)) return onTrack();
  if (rejections != null && rejections >= 2) return fair();
  return fair();
}

function normalizeId(id) {
  if (id == null || id === '') return '';
  return String(id);
}

/**
 * Legacy GET /admin/tasks/analytics often fills employeeStats / statusCounts but omits cadenceBreakdown.
 * Top KPI cards must not rely on cadence rows only.
 */
function aggregateOrgTaskTotals(analytics) {
  if (!analytics || typeof analytics !== 'object') {
    return {
      totalTasks: 0,
      totalCompleted: 0,
      totalInProgress: 0,
      overallRate: '0.0',
      source: 'none',
    };
  }
  const cadence = analytics.cadenceBreakdown || [];
  let totalTasks = cadence.reduce((s, c) => s + (c.totalTasks || 0), 0);
  let totalCompleted = cadence.reduce((s, c) => s + (c.completedTasks || 0), 0);
  let totalInProgress = cadence.reduce((s, c) => s + (c.inProgressTasks || 0), 0);
  let source = 'cadence';

  const hasCadenceTotals = totalTasks > 0 || totalCompleted > 0 || totalInProgress > 0;

  if (!hasCadenceTotals) {
    const sc = analytics.statusCounts || [];
    if (sc.length) {
      const map = {};
      sc.forEach((x) => {
        map[x._id] = Number(x.count) || 0;
      });
      totalTasks = sc.reduce((sum, x) => sum + (Number(x.count) || 0), 0);
      totalCompleted = map.completed || 0;
      totalInProgress =
        (map.assigned || 0) +
        (map['in-progress'] || 0) +
        (map['review-pending'] || 0);
      source = 'statusCounts';
    }
  }

  if (totalTasks === 0 && totalCompleted === 0 && totalInProgress === 0 && (analytics.employeeStats || []).length) {
    let tt = 0;
    let tc = 0;
    let tip = 0;
    (analytics.employeeStats || []).forEach((st) => {
      tt += Number(st.totalTasks) || 0;
      tc += Number(st.completedTasks) || 0;
      tip += Number(st.inProgressTasks) || 0;
    });
    if (tt > 0 || tc > 0 || tip > 0) {
      totalTasks = tt;
      totalCompleted = tc;
      totalInProgress = tip;
      source = 'employeeStats';
    }
  }

  const overallRate =
    totalTasks > 0 ? ((totalCompleted / totalTasks) * 100).toFixed(1) : '0.0';
  return { totalTasks, totalCompleted, totalInProgress, overallRate, source };
}

function findEmployeeStatById(employeeStats, rowId) {
  const id = normalizeId(rowId);
  if (!id) return null;
  return (employeeStats || []).find((s) => normalizeId(s.employeeId) === id) || null;
}

function findPerformanceByEmployeeId(performanceByEmployee, rowId) {
  const id = normalizeId(rowId);
  if (!id) return null;
  return (performanceByEmployee || []).find((p) => normalizeId(p.employeeId) === id) || null;
}

function buildRowFromParts({
  employeeId,
  fullName,
  email,
  completed,
  total,
  inProgress,
  byCadence,
  overdue,
  rejectedReviewCount,
  completedUrgentOrHighCount,
  avgCompletionDays,
  completionRateIfZeroTasks,
}) {
  const rate =
    total > 0
      ? Math.round((completed / total) * 100)
      : completionRateIfZeroTasks != null
        ? Math.round(Number(completionRateIfZeroTasks))
        : 0;
  const rj =
    rejectedReviewCount != null && Number.isFinite(Number(rejectedReviewCount))
      ? Number(rejectedReviewCount)
      : null;
  const ov =
    overdue != null && Number.isFinite(Number(overdue)) ? Number(overdue) : null;
  return {
    employeeId,
    fullName: fullName || 'Unknown',
    email: email || '',
    completed,
    total,
    inProgress,
    rate,
    overdue: ov,
    rejectedReviewCount: rj,
    completedUrgentOrHighCount:
      completedUrgentOrHighCount != null && Number.isFinite(Number(completedUrgentOrHighCount))
        ? Number(completedUrgentOrHighCount)
        : null,
    avgCompletionDays:
      avgCompletionDays != null && Number.isFinite(Number(avgCompletionDays))
        ? Number(avgCompletionDays)
        : null,
    byCadence: byCadence || [],
    signal: hrSignal(rate, ov, rj),
    noTasksInFilter: total === 0,
  };
}

function mergeEmployeeRows(analytics) {
  const statsById = {};
  (analytics.employeeStats || []).forEach((s) => {
    const id = normalizeId(s.employeeId);
    if (id) statsById[id] = { ...s, employeeId: id };
  });
  const list = analytics.performanceByEmployee || [];
  return list.map((emp) => {
    const empId = normalizeId(emp.employeeId);
    const stat = statsById[empId] || {};
    const totalCompleted = (emp.byCadence || []).reduce((s, c) => s + (c.completedTasks || 0), 0);
    const totalTasks = (emp.byCadence || []).reduce((s, c) => s + (c.totalTasks || 0), 0);
    const totalInProgress = (emp.byCadence || []).reduce((s, c) => s + (c.inProgressTasks || 0), 0);
    const completed = stat.completedTasks != null ? stat.completedTasks : totalCompleted;
    const total = stat.totalTasks != null ? stat.totalTasks : totalTasks;
    const inProgress = stat.inProgressTasks != null ? stat.inProgressTasks : totalInProgress;
    const overdue =
      stat.overdueTasks != null
        ? stat.overdueTasks
        : emp.overdueTasks != null
          ? emp.overdueTasks
          : null;
    const rejectedReviewCount =
      stat.rejectedReviewCount != null ? Number(stat.rejectedReviewCount) : null;
    const completedUrgentOrHighCount =
      stat.completedUrgentOrHighCount != null ? Number(stat.completedUrgentOrHighCount) : null;
    const avgCompletionDays =
      stat.avgCompletionDays != null ? Number(stat.avgCompletionDays) : null;
    return buildRowFromParts({
      employeeId: empId || normalizeId(emp.employeeId),
      fullName: emp.employeeName || stat.employeeName,
      email: emp.employeeEmail || stat.employeeEmail,
      completed,
      total,
      inProgress,
      byCadence: emp.byCadence || [],
      overdue,
      rejectedReviewCount,
      completedUrgentOrHighCount,
      avgCompletionDays,
      completionRateIfZeroTasks: stat.completionRate,
    });
  });
}

/** Stats-only row when employee appears in employeeStats but not performanceByEmployee. */
function rowFromEmployeeStatAlone(stat) {
  const id = normalizeId(stat.employeeId);
  if (!id) return null;
  const total = stat.totalTasks != null ? Number(stat.totalTasks) : 0;
  const completed = stat.completedTasks != null ? Number(stat.completedTasks) : 0;
  const inProgress = stat.inProgressTasks != null ? Number(stat.inProgressTasks) : 0;
  return buildRowFromParts({
    employeeId: id,
    fullName: stat.employeeName,
    email: stat.employeeEmail,
    completed,
    total,
    inProgress,
    byCadence: [],
    overdue: stat.overdueTasks != null ? Number(stat.overdueTasks) : null,
    rejectedReviewCount:
      stat.rejectedReviewCount != null ? Number(stat.rejectedReviewCount) : null,
    completedUrgentOrHighCount:
      stat.completedUrgentOrHighCount != null ? Number(stat.completedUrgentOrHighCount) : null,
    avgCompletionDays: stat.avgCompletionDays != null ? Number(stat.avgCompletionDays) : null,
    completionRateIfZeroTasks: stat.completionRate,
  });
}

function isRosterEmployee(userLike) {
  const r = (userLike?.role || 'employee').toString().toLowerCase();
  return r === 'employee';
}

/**
 * Task analytics only include people in assignedTo for matching tasks. HR still needs the full roster:
 * merge performance rows + employeeStats-only + everyone from GET /admin/employees (role employee).
 */
function buildHrEmployeeTableRows(analytics, rosterEmployees) {
  const map = {};
  mergeEmployeeRows(analytics).forEach((row) => {
    const id = normalizeId(row.employeeId);
    if (id) map[id] = row;
  });
  (analytics.employeeStats || []).forEach((s) => {
    const id = normalizeId(s.employeeId);
    if (!id || map[id]) return;
    const row = rowFromEmployeeStatAlone(s);
    if (row) map[id] = row;
  });
  (rosterEmployees || []).forEach((emp) => {
    if (!isRosterEmployee(emp)) return;
    const id = normalizeId(getUserIdentifier(emp));
    if (!id || map[id]) return;
    map[id] = buildRowFromParts({
      employeeId: id,
      fullName: emp.name,
      email: emp.email,
      completed: 0,
      total: 0,
      inProgress: 0,
      byCadence: [],
      overdue: null,
      rejectedReviewCount: null,
      completedUrgentOrHighCount: null,
      avgCompletionDays: null,
    });
  });
  const rosterOrgById = {};
  (rosterEmployees || []).forEach((emp) => {
    const id = normalizeId(getUserIdentifier(emp));
    if (!id) return;
    const raw = emp.organization ?? emp.companyName ?? emp.company;
    const label = raw == null || raw === '' ? '' : String(raw).trim();
    rosterOrgById[id] = label;
  });
  return Object.values(map).map((row) => ({
    ...row,
    companyName: rosterOrgById[row.employeeId] || row.companyName || '',
  }));
}

function FrequencyBreakdownList({ items, title, subtitle }) {
  if (!items.length) return null;
  return (
    <section className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden transition-shadow duration-300 hover:shadow-md animate-fade-in-up">
      <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
      </div>
      <ul className="divide-y divide-gray-100">
        {items.map((row) => {
          const total = row.totalTasks || 0;
          const done = row.completedTasks || 0;
          const prog = row.inProgressTasks || 0;
          const other = Math.max(0, total - done - prog);
          const pct = row.completionRate != null ? Number(row.completionRate).toFixed(0) : total ? ((done / total) * 100).toFixed(0) : '0';
          const label = TASK_FREQUENCY_LABEL[row.cadence] || row.cadence;
          return (
            <li key={row.cadence} className="px-5 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span className="font-medium text-gray-900">{label}</span>
                <span className="text-sm text-gray-600 sm:text-right">
                  <span className="font-semibold text-gray-900">{done}</span> completed
                  {total > 0 && (
                    <>
                      {' '}
                      of <span className="font-semibold text-gray-900">{total}</span> assigned
                    </>
                  )}
                  {' · '}
                  <span className="font-semibold text-indigo-700">{pct}%</span> done
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Still active: {prog} in progress
                {other > 0 ? `, ${other} other (assigned / review / not started)` : ''}.
              </p>
              <div className="mt-2 h-2 w-full rounded-full bg-gray-100 overflow-hidden" aria-hidden>
                <div
                  className="h-full rounded-full bg-indigo-600 transition-all"
                  style={{ width: `${Math.min(100, Number(pct) || 0)}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Backend sends average completion in days; show hours for managers (e.g. 1.5 hr). */
function formatAvgCompletionHoursFromDays(days) {
  if (days == null || !Number.isFinite(Number(days))) return null;
  const hours = Number(days) * 24;
  if (hours < 1) {
    const mins = Math.max(1, Math.round(hours * 60));
    return `${mins} min`;
  }
  const rounded = Math.abs(hours - Math.round(hours)) < 0.05 ? Math.round(hours) : Number(hours.toFixed(1));
  return `${rounded} hr`;
}

function EmployeeDetailBody({ byCadence, teamCadence, hrMetrics, rowSnapshot }) {
  const fromCadence = {
    totalTasks: byCadence.reduce((s, c) => s + (c.totalTasks || 0), 0),
    totalCompleted: byCadence.reduce((s, c) => s + (c.completedTasks || 0), 0),
    totalInProgress: byCadence.reduce((s, c) => s + (c.inProgressTasks || 0), 0),
  };
  const useListSnapshot =
    (!byCadence || byCadence.length === 0) &&
    rowSnapshot &&
    (Number(rowSnapshot.total) > 0 ||
      Number(rowSnapshot.completed) > 0 ||
      Number(rowSnapshot.inProgress) > 0);
  const totalTasks = useListSnapshot ? Number(rowSnapshot.total) || 0 : fromCadence.totalTasks;
  const totalCompleted = useListSnapshot ? Number(rowSnapshot.completed) || 0 : fromCadence.totalCompleted;
  const totalInProgress = useListSnapshot ? Number(rowSnapshot.inProgress) || 0 : fromCadence.totalInProgress;
  const overallPct = useListSnapshot
    ? totalTasks > 0
      ? ((totalCompleted / totalTasks) * 100).toFixed(1)
      : Number(rowSnapshot.rate ?? 0).toFixed(1)
    : totalTasks > 0
      ? ((totalCompleted / totalTasks) * 100).toFixed(1)
      : '0';

  const showHr =
    hrMetrics &&
    (hrMetrics.overdue != null ||
      hrMetrics.rejectedReviewCount != null ||
      hrMetrics.completedUrgentOrHighCount != null ||
      hrMetrics.avgCompletionDays != null);

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-blue-100 bg-blue-50/80 px-4 py-3 text-sm text-blue-900">
        <p className="font-medium">What this means for HR</p>
        <p className="mt-1 text-blue-800/90">
          These numbers come from assigned tasks only. Use completion rate together with conversations and
          other HR data before promotion or salary decisions—not this screen alone.
        </p>
      </div>

      {showHr && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-xs text-gray-500">Overdue tasks</p>
            <p className="text-lg font-bold text-gray-900 mt-0.5">
              {hrMetrics.overdue != null ? hrMetrics.overdue : '—'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-xs text-gray-500">Send-backs (review)</p>
            <p className="text-lg font-bold text-gray-900 mt-0.5">
              {hrMetrics.rejectedReviewCount != null ? hrMetrics.rejectedReviewCount : '—'}
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">reviewDecision rejected</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-xs text-gray-500">Done (high / urgent)</p>
            <p className="text-lg font-bold text-gray-900 mt-0.5">
              {hrMetrics.completedUrgentOrHighCount != null ? hrMetrics.completedUrgentOrHighCount : '—'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-xs text-gray-500">Avg. time to complete</p>
            <p className="text-lg font-bold text-gray-900 mt-0.5">
              {formatAvgCompletionHoursFromDays(hrMetrics.avgCompletionDays) ?? '—'}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {useListSnapshot && !byCadence?.length && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
            A per-frequency breakdown is not in the API response for this person. The three numbers below match the
            employee table (same scope as your admin analytics).
          </div>
        )}

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Tasks assigned</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totalTasks}</p>
          <p className="text-xs text-gray-500 mt-1">For the current frequency filter (if any).</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Finished</p>
          <p className="text-2xl font-bold text-emerald-700 mt-1">{totalCompleted}</p>
          <p className="text-xs text-gray-500 mt-1">Marked completed</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Completion rate</p>
          <p className="text-2xl font-bold text-indigo-700 mt-1">{overallPct}%</p>
          <p className="text-xs text-gray-500 mt-1">{totalInProgress} still in progress</p>
        </div>
      </div>

      {byCadence?.length > 0 ? (
        <FrequencyBreakdownList
          items={byCadence}
          title="Breakdown by how often work is checked"
          subtitle="Daily, weekly, monthly, or yearly—based on each task’s setting."
        />
      ) : (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
          No per-frequency breakdown returned for this employee. When the admin API uses the shared analytics util, this
          section will list daily / weekly / monthly / yearly buckets.
        </div>
      )}

      {teamCadence?.length > 0 && (
        <FrequencyBreakdownList
          items={teamCadence}
          title="Whole team (same filter)"
          subtitle="For context while reviewing this person."
        />
      )}
    </div>
  );
}

const COMPANY_FILTER_ALL = '';
const COMPANY_FILTER_UNSET = '__UNSET__';

const HrPerformanceReview = ({ variant }) => {
  const isSuper = variant === 'superadmin';
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cadenceFilter, setCadenceFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState(COMPANY_FILTER_ALL);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (cadenceFilter) params.set('cadence', cadenceFilter);
      const { data } = await fetchAdminOrSuperadminTaskAnalytics(variant, params);
      setAnalytics(data || {});
    } catch (err) {
      const status = err.response?.status;
      const base = getTaskAnalyticsErrorMessage(err, 'Could not load performance data.');
      const msg =
        status === 403 || status === 401
          ? 'Your account cannot load task analytics. Super Admin needs GET /superadmin/tasks/analytics (or admin access) on the server.'
          : base;
      setError(msg);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [variant, cadenceFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (error || !analytics) return undefined;
    let cancelled = false;
    setRosterLoading(true);
    api
      .get('/admin/employees')
      .then((res) => {
        if (cancelled) return;
        setRoster(normalizeUserProfiles(res.data.employees || []));
      })
      .catch(() => {
        if (!cancelled) setRoster([]);
      })
      .finally(() => {
        if (!cancelled) setRosterLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [analytics, error, variant]);

  const orgTotals = useMemo(() => aggregateOrgTaskTotals(analytics), [analytics]);
  const {
    totalTasks,
    totalCompleted,
    totalInProgress,
    overallRate,
    source: totalsSource,
  } = orgTotals;

  const allTableRows = useMemo(() => {
    if (!analytics) return [];
    return buildHrEmployeeTableRows(analytics, roster);
  }, [analytics, roster]);

  const companyFilterOptions = useMemo(() => {
    const names = new Set();
    let hasUnset = false;
    allTableRows.forEach((r) => {
      const c = (r.companyName || '').trim();
      if (c) names.add(c);
      else hasUnset = true;
    });
    return { names: Array.from(names).sort((a, b) => a.localeCompare(b)), hasUnset };
  }, [allTableRows]);

  const employeeRows = useMemo(() => {
    return allTableRows
      .filter((r) => {
        if (companyFilter === COMPANY_FILTER_ALL) return true;
        if (companyFilter === COMPANY_FILTER_UNSET) return !(r.companyName || '').trim();
        return (r.companyName || '').trim() === companyFilter;
      })
      .filter((r) => {
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return r.fullName.toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q);
      })
      .sort((a, b) => {
        const wa = a.total > 0 ? 1 : 0;
        const wb = b.total > 0 ? 1 : 0;
        if (wa !== wb) return wb - wa;
        if (b.rate !== a.rate) return b.rate - a.rate;
        if (b.completed !== a.completed) return b.completed - a.completed;
        return a.fullName.localeCompare(b.fullName);
      });
  }, [allTableRows, companyFilter, search]);

  const openEmployee = useCallback(
    async (row) => {
      setSelected(row);
      setDetail(null);
      setDetailLoading(true);
      const orgSnapshot = analytics?.cadenceBreakdown || [];
      try {
        const params = new URLSearchParams();
        params.set('employeeId', row.employeeId);
        if (cadenceFilter) params.set('cadence', cadenceFilter);
        const { data } = await fetchAdminOrSuperadminTaskAnalytics(variant, params);
        const rowId = normalizeId(row.employeeId);
        const perf = findPerformanceByEmployeeId(data?.performanceByEmployee, rowId);
        const stat0 = findEmployeeStatById(data?.employeeStats, rowId);
        let byCadence = perf?.byCadence?.length ? perf.byCadence : [];
        if (!byCadence.length && row.byCadence?.length) {
          byCadence = row.byCadence;
        }
        const hrMetrics = stat0
          ? {
              overdue: stat0.overdueTasks != null ? Number(stat0.overdueTasks) : null,
              rejectedReviewCount:
                stat0.rejectedReviewCount != null ? Number(stat0.rejectedReviewCount) : null,
              completedUrgentOrHighCount:
                stat0.completedUrgentOrHighCount != null ? Number(stat0.completedUrgentOrHighCount) : null,
              avgCompletionDays:
                stat0.avgCompletionDays != null ? Number(stat0.avgCompletionDays) : null,
            }
          : {
              overdue: row.overdue,
              rejectedReviewCount: row.rejectedReviewCount,
              completedUrgentOrHighCount: row.completedUrgentOrHighCount,
              avgCompletionDays: row.avgCompletionDays,
            };
        setDetail({ byCadence, teamCadence: orgSnapshot, hrMetrics });
      } catch (err) {
        console.error(err);
        setDetail({
          byCadence: row.byCadence || [],
          teamCadence: orgSnapshot,
          loadError: true,
          hrMetrics: {
            overdue: row.overdue,
            rejectedReviewCount: row.rejectedReviewCount,
            completedUrgentOrHighCount: row.completedUrgentOrHighCount,
            avgCompletionDays: row.avgCompletionDays,
          },
        });
      } finally {
        setDetailLoading(false);
      }
    },
    [analytics?.cadenceBreakdown, cadenceFilter, variant]
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[320px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <AlertCircle className="h-10 w-10 text-red-600 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-red-900">Performance data could not be loaded</h2>
        <p className="text-sm text-red-800 mt-2">{error}</p>
        {isSuper && (
          <p className="text-xs text-red-700/90 mt-4 text-left leading-relaxed">
            Ask your backend to expose <code className="bg-red-100 px-1 rounded">GET /superadmin/tasks/analytics</code> with
            the same JSON as <code className="bg-red-100 px-1 rounded">GET /admin/tasks/analytics</code>, protected for the
            superadmin role. See <code className="bg-red-100 px-1 rounded">docs/BACKEND_PERFORMANCE_API_PROMPT.md</code> in
            this repo.
          </p>
        )}
        <button
          type="button"
          onClick={load}
          className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-700 text-white text-sm font-medium hover:bg-red-800"
        >
          Try again
        </button>
      </div>
    );
  }

  const overdueOrg = analytics?.overdueTasks;

  if (selected) {
    const byCadence = detail?.byCadence || selected.byCadence || [];
    return (
      <div className="space-y-6 max-w-4xl opacity-0 animate-fade-in-up">
        <button
          type="button"
          onClick={() => {
            setSelected(null);
            setDetail(null);
          }}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to all employees
        </button>
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
            {selected.fullName.charAt(0).toUpperCase()}
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{selected.fullName}</h2>
            <p className="text-sm text-gray-500">{selected.email}</p>
            <span
              className={`inline-block mt-2 text-xs font-semibold px-2 py-0.5 rounded border ${selected.signal.className}`}
            >
              Quick signal: {selected.signal.label}
            </span>
          </div>
        </div>
        {detailLoading ? (
          <div className="flex items-center gap-2 text-gray-600 py-8">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading this employee&apos;s detail…
          </div>
        ) : (
          <EmployeeDetailBody
            byCadence={byCadence}
            teamCadence={detail?.teamCadence}
            rowSnapshot={{
              total: selected.total,
              completed: selected.completed,
              inProgress: selected.inProgress,
              rate: selected.rate,
            }}
            hrMetrics={
              detail?.hrMetrics ?? {
                overdue: selected.overdue,
                rejectedReviewCount: selected.rejectedReviewCount,
                completedUrgentOrHighCount: selected.completedUrgentOrHighCount,
                avgCompletionDays: selected.avgCompletionDays,
              }
            }
          />
        )}
        {detail?.loadError && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Could not reload detail from the server; showing list data only.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-10 max-w-7xl pb-12 opacity-0 animate-fade-in-up">
      <div className="relative">
        <div className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-indigo-50/80 via-white to-violet-50/50 blur-2xl opacity-70" aria-hidden />
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Performance &amp; reviews</h1>
        <p className="text-sm text-gray-500 mt-2 max-w-2xl leading-relaxed">
          Snapshot of task completion and workload. Open an employee to see more detail.
        </p>
      </div>

      {totalsSource !== 'cadence' && totalsSource !== 'none' && totalTasks > 0 && (
        <p className="text-xs text-gray-600 bg-gray-100 border border-gray-200 rounded-lg px-3 py-2">
          Top summary uses <strong>{totalsSource === 'statusCounts' ? 'statusCounts' : 'employeeStats'}</strong> because{' '}
          <code className="text-xs bg-white px-1 rounded border">cadenceBreakdown</code> is missing or empty. Align{' '}
          <code className="text-xs bg-white px-1 rounded border">GET /admin/tasks/analytics</code> with the shared{' '}
          <code className="text-xs bg-white px-1 rounded border">getTaskAdminAnalytics</code> util for consistent
          frequency KPIs and filters.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: '0ms' }}>
          <SummaryCard
            icon={CalendarDays}
            title="Completed this week"
            value={analytics?.completedThisWeek ?? '—'}
            hint="Tasks finished since the start of this calendar week (with your filters)."
          />
        </div>
        <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: '75ms' }}>
          <SummaryCard
            icon={CalendarRange}
            title="Completed this month"
            value={analytics?.completedThisMonth ?? '—'}
            hint="Tasks finished since the start of this calendar month (with your filters)."
          />
        </div>
        <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: '150ms' }}>
          <SummaryCard
            icon={AlertCircle}
            title="Overdue (org)"
            value={overdueOrg != null ? overdueOrg : '—'}
            hint="Tasks past due date and not yet completed (whole team, with your filters)."
          />
        </div>
        <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: '225ms' }}>
          <SummaryCard
            icon={Clock}
            title="Avg. completion time"
            value={formatAvgCompletionHoursFromDays(analytics?.avgCompletionDays) ?? '—'}
            hint="Typical time from assignment to completion, shown in hours."
          />
        </div>
      </div>

      <p className="text-xs text-gray-500 -mt-2">
        Workload snapshot: <strong>{totalCompleted}</strong> completed / <strong>{totalTasks}</strong> tasks in view ·{' '}
        <strong>{totalInProgress}</strong> in progress · <strong>{overallRate}%</strong> team completion rate
        {totalsSource !== 'cadence' && totalsSource !== 'none' && (
          <span className="text-amber-700">
            {' '}
            (totals from {totalsSource === 'statusCounts' ? 'statusCounts' : 'employeeStats'} when the frequency breakdown
            is empty)
          </span>
        )}
        .
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '280ms' }}>
        <StatusCountsChart statusCounts={analytics?.statusCounts} />
        <PriorityCountsChart priorityCounts={analytics?.priorityCounts} />
      </div>

      <section
        className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06),0_12px_32px_-8px_rgba(79,70,229,0.12)] overflow-hidden opacity-0 animate-fade-in-up transition-shadow duration-300 hover:shadow-[0_1px_3px_rgba(0,0,0,0.08),0_16px_40px_-8px_rgba(79,70,229,0.16)]"
        style={{ animationDelay: '340ms' }}
      >
        <div className="px-5 sm:px-6 py-5 sm:py-6 border-b border-gray-100 bg-gradient-to-br from-slate-50/90 via-white to-indigo-50/40">
          <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5 xl:gap-8">
            <div className="min-w-0 flex-1">
              <h3 className="text-xl font-semibold text-gray-900 tracking-tight">Employee comparison</h3>
              <p className="text-sm text-gray-600 mt-1.5 leading-relaxed max-w-xl">
                <strong>0 / 0</strong> means no tasks match the filters—use <span className="font-medium text-gray-800">View</span>{' '}
                for full detail.
                {rosterLoading && (
                  <span className="block mt-2 text-indigo-600 text-xs font-medium">Loading team roster…</span>
                )}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-3 w-full xl:w-auto xl:max-w-none shrink-0">
              <label className="flex flex-col gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider sm:min-w-[140px]">
                Frequency
                <select
                  value={cadenceFilter}
                  onChange={(e) => setCadenceFilter(e.target.value)}
                  className="normal-case font-medium text-gray-900 border border-gray-200/90 rounded-xl px-3 py-2.5 bg-white/90 backdrop-blur-sm shadow-sm text-sm transition-all duration-200 hover:border-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-400"
                >
                  {FREQUENCY_FILTER_OPTIONS.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider sm:min-w-[160px]">
                Company
                <select
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                  className="normal-case font-medium text-gray-900 border border-gray-200/90 rounded-xl px-3 py-2.5 bg-white/90 backdrop-blur-sm shadow-sm text-sm transition-all duration-200 hover:border-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-400"
                >
                  <option value={COMPANY_FILTER_ALL}>All companies</option>
                  {companyFilterOptions.names.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                  {companyFilterOptions.hasUnset && (
                    <option value={COMPANY_FILTER_UNSET}>No company on file</option>
                  )}
                </select>
              </label>
              <label className="flex flex-col gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider flex-1 sm:min-w-[200px] sm:max-w-xs">
                Find employee
                <span className="relative normal-case">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Name or email"
                    className="w-full font-medium text-gray-900 border border-gray-200/90 rounded-xl pl-9 pr-3 py-2.5 bg-white/90 backdrop-blur-sm shadow-sm text-sm transition-all duration-200 hover:border-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-400 placeholder:text-gray-400 placeholder:font-normal"
                  />
                </span>
              </label>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gradient-to-b from-slate-50 to-slate-50/80 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                <th className="px-4 py-3.5 font-bold w-10">#</th>
                <th className="px-4 py-3.5 font-bold max-w-[120px]">Company</th>
                <th className="px-4 py-3.5 font-bold min-w-[160px]">Employee</th>
                <th className="px-4 py-3.5 font-bold whitespace-nowrap">Done / total</th>
                <th className="px-4 py-3.5 font-bold min-w-[100px]">Rate</th>
                <th className="px-4 py-3.5 font-bold whitespace-nowrap">Active</th>
                <th className="px-4 py-3.5 font-bold whitespace-nowrap">Overdue</th>
                <th className="px-4 py-3.5 font-bold whitespace-nowrap" title="Tasks sent back after review">
                  Send-backs
                </th>
                <th className="px-4 py-3.5 font-bold whitespace-nowrap">Avg time</th>
                <th className="px-4 py-3.5 font-bold">Signal</th>
                <th className="px-4 py-3.5 font-bold text-right sticky right-0 z-20 bg-gradient-to-b from-slate-50 to-slate-50/95 border-l border-gray-200/80 shadow-[-6px_0_12px_-4px_rgba(15,23,42,0.08)] min-w-[108px]">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100/90">
              {employeeRows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-14 text-center text-gray-500">
                    {rosterLoading
                      ? 'Loading employees…'
                      : 'No employees found. Check the Employees page or your account access to GET /admin/employees.'}
                  </td>
                </tr>
              ) : (
                employeeRows.map((row, rowIndex) => (
                  <tr
                    key={row.employeeId}
                    className="group hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-white transition-colors duration-200"
                  >
                    <td className="px-4 py-3.5 text-gray-400 tabular-nums text-xs font-medium">{rowIndex + 1}</td>
                    <td className="px-4 py-3.5 text-gray-600 max-w-[120px]">
                      <span className="line-clamp-2 text-xs leading-snug">
                        {(row.companyName || '').trim() || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="font-semibold text-gray-900">{row.fullName}</div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[220px]">{row.email}</div>
                      {row.noTasksInFilter && (
                        <div className="text-[11px] text-amber-700 mt-1 font-medium">No tasks in filter</div>
                      )}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className="font-bold text-emerald-600 tabular-nums">{row.completed}</span>
                      <span className="text-gray-300 mx-0.5">/</span>
                      <span className="font-semibold text-gray-800 tabular-nums">{row.total}</span>
                    </td>
                    <td className="px-4 py-3.5 min-w-[96px]">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden ring-1 ring-inset ring-gray-200/60">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-[width] duration-500 ease-out"
                            style={{ width: `${Math.min(100, row.rate)}%` }}
                          />
                        </div>
                        <span className="font-bold text-gray-900 w-9 text-right text-xs tabular-nums">{row.rate}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-gray-700 tabular-nums font-medium">{row.inProgress}</td>
                    <td className="px-4 py-3.5 tabular-nums font-medium">
                      {row.overdue != null ? (
                        <span className={row.overdue > 0 ? 'text-red-600' : 'text-gray-700'}>{row.overdue}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3.5 tabular-nums text-gray-700">
                      {row.rejectedReviewCount != null ? row.rejectedReviewCount : '—'}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-gray-700 text-xs font-medium">
                      {formatAvgCompletionHoursFromDays(row.avgCompletionDays) ?? '—'}
                    </td>
                    <td className="px-4 py-3.5">
                      <span
                        className={`inline-flex text-[11px] font-bold px-2.5 py-1 rounded-lg border shadow-sm ${row.signal.className}`}
                      >
                        {row.signal.label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right sticky right-0 z-10 bg-white group-hover:bg-indigo-50/70 border-l border-gray-100 shadow-[-8px_0_16px_-6px_rgba(15,23,42,0.12)] transition-colors duration-200 min-w-[108px]">
                      <button
                        type="button"
                        onClick={() => openEmployee(row)}
                        className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-3 py-2 text-xs font-bold text-white shadow-sm transition-all duration-200 hover:bg-indigo-700 hover:shadow-md active:scale-[0.98]"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

function SummaryCard({ icon: Icon, title, value, hint }) {
  return (
    <div className="group relative rounded-2xl border border-gray-100/90 bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all duration-300 hover:shadow-[0_8px_24px_-6px_rgba(79,70,229,0.15)] hover:border-indigo-100 h-full overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-indigo-400 via-violet-400 to-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">{title}</p>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2 tabular-nums tracking-tight">{value}</p>
          <p className="text-xs text-gray-500 mt-2 leading-relaxed">{hint}</p>
        </div>
        <div className="p-3 rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 text-indigo-600 flex-shrink-0 transition-transform duration-300 group-hover:scale-105 ring-1 ring-indigo-100/80">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

export default HrPerformanceReview;
